(() => {
  'use strict';
  const initDraftPricing = (root, config) => {
    const prices = config.previewPrices;
    if (config.version !== 1 || config.currency !== 'AUD' || !prices || Object.keys(prices).length !== 2 || !['Small', 'Medium'].every(size => Number.isSafeInteger(prices[size]) && prices[size] > 0)) return;
    let strings, assets;
    try {
      strings = JSON.parse(root.querySelector('#tc-commerce-i18n')?.textContent || '{}');
      assets = JSON.parse(root.querySelector('#tc-photo-assets')?.textContent || '{}');
    } catch (_) { return; }
    const colours = assets.colours?.map(colour => colour.name);
    if (!Array.isArray(colours) || colours.length !== 21 || new Set(colours).size !== 21) return;
    const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), strings[key] || key);
    const money = amount => `${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount / 100)} ${t('aud')}`;
    const lifecycle = new AbortController();
    const mountingInputs = [...root.querySelectorAll('[data-draft-mounting] input')];
    const mountingKey = 'together-created:mounting:v1';
    try {
      const saved = window.localStorage.getItem(mountingKey);
      mountingInputs.forEach(input => { input.checked = input.value === saved; });
    } catch (_) {}
    mountingInputs.forEach(input => input.addEventListener('change', () => {
      try { window.localStorage.setItem(mountingKey, input.value); } catch (_) {}
    }, { signal: lifecycle.signal }));
    const originals = new Map();
    const remember = el => { if (!originals.has(el)) originals.set(el, { text: el.textContent, hidden: el.hidden }); };
    const setText = (selector, text) => root.querySelectorAll(selector).forEach(el => { remember(el); el.textContent = text; });
    const quote = selection => {
      const name = typeof selection?.name === 'string' ? selection.name.trim().replace(/ +/g, ' ').toUpperCase() : '';
      const letters = [...name].filter(letter => letter !== ' ');
      const quantity = selection?.quantity ?? 1;
      const choices = selection?.choices;
      const ready = selection?.valid !== false && /^[A-Z ]+$/.test(name) && letters.length > 0 && letters.length <= 20 && Number.isSafeInteger(quantity) && quantity > 0 && Array.isArray(choices) && choices.length === letters.length && choices.every((choice, index) => choice?.letter === letters[index] && Object.hasOwn(prices, choice.size) && colours.includes(choice.colour));
      if (!ready) return null;
      const total = choices.reduce((sum, choice) => sum + prices[choice.size], 0) * quantity;
      return Number.isSafeInteger(total) ? { total, quantity, choices } : null;
    };
    const render = selection => {
      const estimate = quote(selection);
      const complete = !!estimate;
      const amount = complete ? money(estimate.total) : t('price_pending');
      setText('[data-commerce-product-price]', complete ? t('name_total', { amount }) : t('choose'));
      setText('[data-commerce-quote],[data-commerce-sticky-price]', amount);
      setText('[data-commerce-quote-note]', complete ? t('preview_note') : t('choose'));
      root.querySelectorAll('[data-commerce-quote-lines]').forEach(list => {
        remember(list);
        list.hidden = !complete;
        list.replaceChildren();
        if (!complete) return;
        estimate.choices.forEach((choice, index) => {
          const item = document.createElement('li');
          const label = document.createElement('span');
          label.textContent = t('line_description', { position: index + 1, letter: choice.letter, size: t(`size_${choice.size.toLowerCase()}`), colour: choice.colour });
          const value = document.createElement('strong');
          value.textContent = t('unit_price', { amount: money(prices[choice.size]) });
          item.append(label, value);
          list.append(item);
        });
      });
    };
    root.addEventListener('tc:selection-change', event => render(event.detail), { signal: lifecycle.signal });
    root.tcDraftPricing = {
      quote, money, pending: () => t('price_pending'),
      destroy() {
        lifecycle.abort();
        originals.forEach((original, el) => { el.textContent = original.text; el.hidden = original.hidden; });
        if (root.tcDraftPricing === this) delete root.tcDraftPricing;
      }
    };
    render(root.tcBuilder?.getState());
  };
  const init = () => {
    const root = document.querySelector('[data-tc-storefront]');
    if (!root) return;
    root.tcCommerceUI?.destroy();
    root.tcDraftPricing?.destroy();
    let config;
    try { config = JSON.parse(root.querySelector('#tc-commerce-config')?.textContent || '{}'); } catch (_) { return; }
    if (config.enabled !== true) { initDraftPricing(root, config); return; }
    if (typeof window.createTCCommerceAdapter !== 'function') return;
    let strings, assets;
    try {
      strings = JSON.parse(root.querySelector('#tc-commerce-i18n')?.textContent || '{}');
      assets = JSON.parse(root.querySelector('#tc-photo-assets')?.textContent || '{}');
    } catch (_) { return; }
    const adapter = window.createTCCommerceAdapter(config, assets);
    const lifecycle = new AbortController();
    const on = (el, name, fn) => el?.addEventListener(name, fn, { signal: lifecycle.signal });
    const t = (key, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(String(value)), strings[key] || key);
    const money = amount => `${new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount / 100)} ${t('aud')}`;
    const clone = value => JSON.parse(JSON.stringify(value));
    const bag = root.querySelector('[data-bag-modal]');
    const hosts = [...root.querySelectorAll('[data-commerce-cart]')];
    const drafts = window.getTCNameDrafts?.(root);
    const originals = new Map();
    const remember = el => {
      if (el && !originals.has(el)) originals.set(el, { text: el.textContent, hidden: el.hidden, disabled: el.disabled, label: el.getAttribute('aria-label') });
      return el;
    };
    const setText = (selector, value) => root.querySelectorAll(selector).forEach(el => { remember(el); originals.get(el).restoreText = true; el.textContent = value; });
    const setHidden = (selector, hidden) => root.querySelectorAll(selector).forEach(el => { remember(el); el.hidden = hidden; });
    const create = (tag, content, className) => {
      const el = document.createElement(tag);
      if (content !== undefined) el.textContent = content;
      if (className) el.className = className;
      return el;
    };
    const button = (label, action, id, className = 'tc-outline') => {
      const el = create('button', label, className);
      el.type = 'button'; el.dataset.commerceAction = action;
      if (id) el.dataset.commerceGroup = id;
      el.disabled = busy;
      return el;
    };
    const link = (label, href, className = 'tc-text') => {
      const el = create('a', label, className); el.href = href; return el;
    };
    const contactUrl = () => {
      try { const path = new URL(bag?.dataset.builderUrl || '/', window.location.origin).pathname; return `${path}pages/contact`; } catch (_) { return '/pages/contact'; }
    };
    const builderUrl = bag?.dataset.builderUrl || '/#create';
    let cart = null;
    let view = null;
    let busy = false;
    let stale = true;
    let errorMessage = '';
    let lastErrorCode = '';
    let notice = '';
    let editId = null;
    let opener;
    let destroyed = false;
    let selection = root.tcBuilder?.getState();
    let pendingRefresh = null;
    let mountingChoice = '';
    const mountingOptions = ['Permanent fixing', 'Temporary fixing'];
    const mountingPanel = root.querySelector('[data-product-mounting]');
    const hasHeight = (size, groups = view?.groups || []) => groups.some(group => group.choices.some(choice => choice.size === size));
    const selectedHeight = () => selection?.size || selection?.choices?.[0]?.size;
    try { mountingChoice = window.localStorage.getItem('together-created:mounting:v1') || ''; } catch (_) {}
    if (!mountingOptions.includes(mountingChoice)) mountingChoice = '';
    let confirmedStorageBlocked = false;
    const recovery = () => { try { return adapter.getRecovery(); } catch (error) { return ['INVALID_RECOVERY_RECORD','RECOVERY_STORAGE_UNAVAILABLE'].includes(error.code) ? { outcome: 'unknown', safeToRepair: false, groups: [] } : null; } };
    const errorText = error => {
      if (error?.replacementRemoved) return t('replacement_removed');
      const map = {
        MOUNTING_REQUIRED:'mounting_required', MOUNTING_UNCONFIRMED:'mounting_failed',
        COMMERCE_DISABLED:'unavailable', INVALID_CONFIG:'unavailable', INVALID_VARIANT_MAP:'unavailable', INVALID_COLOUR_MANIFEST:'unavailable', INVALID_ROUTES:'unavailable',
        UNSUPPORTED_BROWSER:'unsupported', RECOVERY_STORAGE_UNAVAILABLE:'storage', INVALID_RECOVERY_RECORD:'uncertain',
        INCOMPLETE_GROUP:'incomplete', INVALID_GROUP:'incomplete', VARIANT_UNAVAILABLE:'stock', ADD_REJECTED:'rejected',
        ADDITION_UNCERTAIN:'uncertain', MUTATION_UNCERTAIN:'uncertain', CART_REVIEW_REQUIRED:'cart_invalid', INVALID_QUANTITY:'invalid_quantity',
        INVALID_CART_RESPONSE:'cart_invalid', CART_CHANGED:'checkout_changed', EMPTY_CART:'empty', CHECKOUT_UNAVAILABLE:'cart_invalid'
      };
      return t(map[error?.code] || 'network');
    };
    const validTotal = amount => Number.isSafeInteger(amount) && amount >= 0;
    const acceptCart = value => {
      const inspected = adapter.inspect(value);
      if (![inspected.subtotal, inspected.discount, inspected.total].every(validTotal) || inspected.currency !== 'AUD') throw Object.assign(new Error(), { code: 'CART_REVIEW_REQUIRED' });
      cart = value; view = inspected; stale = false;
      if (hasHeight('Medium') && mountingOptions.includes(cart.attributes?.['Mounting method'])) mountingChoice = cart.attributes['Mounting method'];
      root.querySelectorAll('[data-bag-open]').forEach(el => { remember(el); el.setAttribute('aria-label', t('bag_label', { count: Number.isSafeInteger(value.item_count) ? value.item_count : value.items.reduce((sum, item) => sum + item.quantity, 0) })); });
    };
    const activeGroup = () => {
      if (!selection) return null;
      return { ...clone(selection), id: selection.id || 'quote', quantity: selection.quantity || 1 };
    };
    const renderMounting = () => {
      if (!mountingPanel) return;
      const medium = selectedHeight() === 'Medium';
      mountingPanel.hidden = !medium;
      const smallNote = root.querySelector('[data-small-mounting]');
      if (smallNote) smallNote.hidden = selectedHeight() !== 'Small';
      if (!mountingPanel.firstElementChild) {
        const fieldset = create('fieldset', undefined, 'tc-mounting tc-product-mounting');
        fieldset.append(create('legend', t('mounting_title')), create('p', t('mounting_note'), 'tc-small'));
        [['permanent', 'Permanent fixing'], ['temporary', 'Temporary fixing']].forEach(([key, value]) => {
          const label = create('label'), input = create('input');
          input.type = 'radio'; input.name = 'product-mounting'; input.value = value; input.dataset.commerceMounting = '';
          const text = create('span'); text.append(create('strong', t(`mounting_${key}`)), create('small', t(`mounting_${key}_detail`)));
          label.append(input, text); fieldset.append(label);
        });
        mountingPanel.append(fieldset);
      }
      mountingPanel.querySelectorAll('input').forEach(input => {
        input.checked = input.value === mountingChoice;
        input.disabled = !medium || busy || !!recovery();
        input.required = medium;
      });
    };
    const renderQuote = () => {
      renderMounting();
      let quote = null;
      let quoteError = '';
      try { quote = adapter.quote(activeGroup()); }
      catch (error) { quoteError = errorText(error); }
      const pending = recovery();
      const availability = adapter.availability();
      const mountingReady = selectedHeight() !== 'Medium' || mountingOptions.includes(mountingChoice);
      const ready = availability.ready && !pending && !busy && quote && mountingReady;
      root.querySelectorAll('[data-demo-add]').forEach(el => {
        remember(el); originals.get(el).restoreText = true; el.textContent = t('add'); el.disabled = !ready;
      });
      const amount = quote ? money(quote.total) : t('price_pending');
      setText('[data-commerce-product-price]', quote ? t('name_total', { amount }) : t('choose'));
      setText('[data-commerce-quote],[data-commerce-sticky-price]', amount);
      setText('[data-commerce-quote-note]', quote ? t('quote_note', { quantity: quote.quantity }) : quoteError || t('choose'));
      if (!availability.ready && !pending) {
        setText('[data-commerce-quote-note]', errorText({ code: availability.reason || availability.code || 'INVALID_CONFIG' }));
      }
      if (quote && !mountingReady) setText('[data-commerce-quote-note]', t('mounting_required'));
      if (pending) setText('[data-commerce-quote-note]', t('uncertain'));
      root.querySelectorAll('[data-commerce-quote-lines]').forEach(list => {
        list.hidden = !quote;
        list.replaceChildren();
        quote?.letters.forEach((letter, index) => {
          const item = create('li');
          item.append(create('span', t('line_description', { position: index + 1, letter: letter.letter, size: t(`size_${letter.size.toLowerCase()}`), colour: letter.colour })), create('strong', t('unit_price', { amount: money(letter.unitPrice) })));
          list.append(item);
        });
      });
    };
    const focusAction = token => {
      if (!token) return;
      const host = hosts[token.host];
      if (!host) return;
      if (token.action === '_error' || token.action === '_status') { host.querySelector(token.action === '_error' ? '[data-commerce-error]:not([hidden])' : '[data-commerce-status]')?.focus(); return; }
      const match = [...host.querySelectorAll('[data-commerce-action]')].find(el => el.dataset.commerceAction === token.action && (el.dataset.commerceGroup || '') === token.group && !el.disabled);
      (match || host.querySelector('[data-commerce-error]:not([hidden])') || host.querySelector('[data-commerce-status]'))?.focus();
    };
    const focusedToken = () => {
      const el = document.activeElement;
      if (el?.matches('[data-commerce-error],[data-commerce-status]')) return { host: hosts.findIndex(host => host.contains(el)), action: el.hasAttribute('data-commerce-error') ? '_error' : '_status', group: '' };
      if (!el?.dataset.commerceAction) return null;
      return { host: hosts.findIndex(host => host.contains(el)), action: el.dataset.commerceAction, group: el.dataset.commerceGroup || '' };
    };
    const render = () => {
      if (destroyed) return;
      const token = focusedToken();
      const shippingFocus = root.tcShippingUI?.captureFocus();
      const pending = recovery();
      const blocked = busy || stale || !!pending || !!view?.issues?.length;
      hosts.forEach((host, hostIndex) => {
        host.hidden = false;
        host.setAttribute('aria-busy', String(busy));
        const content = document.createDocumentFragment();
        let status = host.querySelector('[data-commerce-status]');
        let error = host.querySelector('[data-commerce-error]');
        let body = host.querySelector('[data-commerce-body]');
        if (!status || !error || !body) {
          status = create('p', '', 'tc-small'); status.dataset.commerceStatus = ''; status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite'); status.tabIndex = -1;
          error = create('p', '', 'tc-commerce-error'); error.dataset.commerceError = ''; error.setAttribute('role', 'alert'); error.tabIndex = -1;
          body = create('div'); body.dataset.commerceBody = '';
          host.replaceChildren(status, error, body);
        }
        status.textContent = busy ? t('loading') : notice;
        const visibleError = errorMessage || (view?.issues?.length ? t('cart_invalid') : '');
        error.textContent = visibleError; error.hidden = !visibleError;
        if (view?.issues?.length) content.append(link(t('help'), contactUrl()));
        if (pending) {
          const box = create('section', undefined, 'tc-commerce-recovery');
          const title = create('h3', t('recovery_title')); title.id = `tc-recovery-${hostIndex}`;
          box.setAttribute('aria-labelledby', title.id);
          box.append(title, create('p', t(confirmedStorageBlocked ? 'recovery_confirmed' : pending.safeToRepair ? 'recovery_known' : 'recovery_unknown')));
          const names = [...new Set((pending.groups || []).map(group => group.name).filter(Boolean))];
          if (names.length) box.append(create('p', t('recovery_names', { names: names.join(', ') }), 'tc-small'));
          box.append(button(t('retry'), 'refresh'));
          if (pending.safeToRepair && !confirmedStorageBlocked) {
            box.append(button(t('recovery_finish'), 'repair'), create('p', t('recovery_remove_note'), 'tc-small'), button(t('recovery_remove'), 'discard'));
          }
          box.append(link(t('help'), contactUrl()));
          content.append(box);
        }
        if (view) {
          if (!view.groups.length && !view.otherItems.length) content.append(create('p', t('empty')));
          view.groups.forEach(group => {
            const card = create('section', undefined, 'tc-cart-item tc-commerce-group'); card.dataset.commerceName = group.id;
            const heading = create('h3', group.name); heading.id = `tc-commerce-name-${hostIndex}-${group.id}`;
            card.setAttribute('aria-labelledby', heading.id); card.append(heading);
            const list = create('ol', undefined, 'tc-commerce-lines');
            group.lines.forEach((line, index) => {
              const properties = line.properties || {};
              const choice = group.choices?.[index] || {};
              const item = create('li');
              const letter = properties.Letter || choice.letter || '';
              const colourName = properties.Colour || choice.colour || '';
              const colour = assets.colours?.find(value => value.name === colourName);
              const sheet = /^[A-Z]$/.test(letter) ? assets.sheets?.[letter] : null;
              if (sheet && /^https:\/\/cdn\.shopify\.com\//.test(sheet) && assets.grid === 5 && Number.isInteger(colour?.tile) && colour.tile >= 0 && colour.tile < 21) {
                const photo = create('span', undefined, 'tc-commerce-letter-photo');
                photo.setAttribute('aria-hidden', 'true');
                photo.style.backgroundImage = `url("${sheet}")`;
                photo.style.backgroundPosition = `${(colour.tile % 5) * 25}% ${Math.floor(colour.tile / 5) * 25}%`;
                item.append(photo);
              }
              item.append(create('span', t('line_description', { position: properties['Letter position'] || index + 1, letter, size: group.complete && choice.size ? t(`size_${choice.size.toLowerCase()}`) : properties.Size || '', colour: colourName }), 'tc-commerce-line-description'));
              if (validTotal(line.final_line_price)) item.append(create('strong', money(line.final_line_price)));
              list.append(item);
            });
            card.append(list);
            if (validTotal(group.total)) card.append(create('p', money(group.total), 'tc-commerce-group-total'));
            if (!group.complete) card.append(create('p', t('incomplete_group'), 'tc-commerce-error'));
            const form = create('form', undefined, 'tc-commerce-quantity'); form.dataset.commerceQuantity = group.id;
            const quantityId = `tc-commerce-qty-${hostIndex}-${group.id}`;
            const label = create('label', t('quantity')); label.htmlFor = quantityId;
            const input = create('input'); input.type = 'number'; input.min = '1'; input.step = '1'; input.inputMode = 'numeric'; input.value = String(group.quantity || 1); input.id = quantityId;
            input.name = 'quantity'; input.required = true; input.setAttribute('aria-label', t('quantity_for', { name: group.name }));
            const update = button(t('update'), 'quantity', group.id); update.type = 'submit';
            input.disabled = update.disabled = busy || !!pending || !group.complete || stale;
            form.append(label, input, update); card.append(form);
            const actions = create('div', undefined, 'tc-bag-actions');
            const edit = button(t('edit'), 'edit', group.id, 'tc-text'); edit.setAttribute('aria-label', t('edit_label', { name: group.name })); edit.disabled = busy || !!pending || !group.complete || stale;
            const remove = button(t('remove'), 'remove', group.id, 'tc-text'); remove.setAttribute('aria-label', t('remove_label', { name: group.name })); remove.disabled = busy || !!pending || stale;
            actions.append(edit, remove); card.append(actions);
            if (editId === group.id) {
              const confirm = create('div', undefined, 'tc-commerce-confirm'); confirm.setAttribute('role', 'group');
              const title = create('h4', t('edit_title', { name: group.name })); title.id = `tc-edit-title-${hostIndex}-${group.id}`;
              confirm.setAttribute('aria-labelledby', title.id);
              confirm.append(title, create('p', t('edit_warning')), button(t('edit_confirm'), 'confirm-edit', group.id), button(t('cancel'), 'cancel-edit', group.id, 'tc-text'));
              card.append(confirm);
            }
            content.append(card);
          });
          if (view.otherItems.length) {
            const other = create('section', undefined, 'tc-commerce-other'); other.append(create('h3', t('other_items')));
            view.otherItems.forEach(item => {
              const row = create('div', undefined, 'tc-commerce-other-item'); row.append(create('p', item.product_title || item.title || t('other_items')), create('span', t('item_quantity', { quantity: item.quantity })));
              if (validTotal(item.final_line_price)) row.append(create('strong', money(item.final_line_price)));
              other.append(row);
            });
            content.append(other);
          }
          if (cart.items.length) {
            const summary = create('div', undefined, 'tc-mounting-summary');
            summary.append(create('strong', t('mounting_summary')));
            if (hasHeight('Small')) summary.append(create('p', t('mounting_small_note'), 'tc-small'));
            if (hasHeight('Medium')) {
              const method = cart.attributes?.['Mounting method'];
              summary.append(create('p', mountingOptions.includes(method) ? t('mounting_medium_summary', { method: t(method === 'Permanent fixing' ? 'mounting_permanent' : 'mounting_temporary') }) : t('mounting_required'), 'tc-small'));
              const change = link(t('mounting_change'), builderUrl); change.dataset.mountingChange = ''; summary.append(change);
            }
            content.append(summary);
          }
          content.append(create('p', t('review_order'), 'tc-small'));
          const totals = create('div', undefined, 'tc-cart-totals');
          const totalRow = (label, amount) => { const row = create('div'); row.append(create('span', label), create('strong', money(amount))); return row; };
          totals.append(totalRow(t('subtotal'), view.subtotal));
          if (view.discount) totals.append(totalRow(t('discount'), view.discount));
          totals.append(totalRow(t('total'), view.total), create('p', t('delivery_note'), 'tc-small'));
          if (cart.items.length && !blocked) {
            const remaining = Math.max(0, 7500 - view.total);
            const postage = create('p', remaining ? t('free_postage_remaining', { amount: money(remaining) }) : t('free_postage_qualified'), 'tc-free-postage');
            postage.dataset.freePostage = remaining ? 'remaining' : 'qualified';
            totals.append(postage);
          }
          const checkout = button(t(busy ? 'checking_checkout' : 'checkout'), 'checkout', null, 'tc-primary');
          checkout.disabled = blocked || !cart?.items?.length || (hasHeight('Medium') && !mountingOptions.includes(cart.attributes?.['Mounting method']));
          totals.append(checkout); content.append(totals);
        } else if (!busy) content.append(create('p', t('read_failed'), 'tc-small'));
        if (!pending) content.append(button(t('refresh'), 'refresh', null, 'tc-text'));
        const another = link(t('continue'), builderUrl, 'tc-outline'); another.dataset.commerceContinue = ''; content.append(another);
        body.replaceChildren(content);
      });
      renderQuote();
      root.tcShippingUI?.sync({ cart, busy, stale, blocked }, shippingFocus);
      focusAction(token);
    };
    const setProductMessage = (value, error = false) => {
      const selector = error ? '[data-commerce-product-error]' : '[data-commerce-product-status]';
      setText(selector, value); setHidden(selector, !value);
    };
    const exposeError = error => {
      errorMessage = errorText(error);
      lastErrorCode = error.code || '';
      if (error.cart) { try { acceptCart(error.cart); } catch (_) { stale = true; } }
      render();
      const currentHost = bag?.open ? bag.querySelector('[data-commerce-cart]') : root.querySelector('[data-commerce-page] [data-commerce-cart]');
      currentHost?.querySelector('[data-commerce-error]')?.focus();
    };
    const refresh = () => {
      if (pendingRefresh) return pendingRefresh;
      if (busy) return Promise.resolve(null);
      busy = true; errorMessage = ''; render();
      pendingRefresh = (async () => {
        try {
          const result = await adapter.reconcile();
          if (destroyed) return null;
          acceptCart(result.cart || await adapter.readCart()); confirmedStorageBlocked = result.recoveryCleared === false; notice = '';
          return cart;
        } catch (error) {
          if (!destroyed) { stale = !error.cart; exposeError(error); }
          return null;
        } finally {
          busy = false; pendingRefresh = null;
          render();
        }
      })();
      return pendingRefresh;
    };
    const openBag = async () => {
      opener = document.activeElement;
      if (bag && !bag.open) bag.showModal();
      await refresh();
    };
    const run = async (operation, onSuccess) => {
      if (busy) return null;
      busy = true; errorMessage = ''; lastErrorCode = ''; notice = ''; editId = null; render();
      try {
        const result = await operation();
        if (destroyed) return null;
        confirmedStorageBlocked = result?.recoveryCleared === false;
        if (result?.cart) acceptCart(result.cart);
        else acceptCart(await adapter.readCart());
        await onSuccess?.(result);
        return result;
      } catch (error) {
        if (!destroyed) { stale = !error.cart; exposeError(error); }
        return null;
      } finally { busy = false; render(); }
    };
    const addCurrent = async state => {
      if (busy) return;
      selection = state || root.tcBuilder?.getState();
      let group;
      try {
        adapter.quote(activeGroup());
        if (selectedHeight() === 'Medium' && !mountingOptions.includes(mountingChoice)) throw Object.assign(new Error(), { code: 'MOUNTING_REQUIRED' });
        group = drafts?.save(selection);
        if (!group) throw Object.assign(new Error(), { code: 'INCOMPLETE_GROUP' });
      } catch (error) {
        setProductMessage(errorText(error), true); root.querySelector('[data-commerce-product-error]')?.focus(); renderQuote(); return;
      }
      setProductMessage('', true);
      const method = selectedHeight() === 'Medium' ? mountingChoice : (hasHeight('Medium') ? cart.attributes?.['Mounting method'] : 'Temporary fixing');
      const result = await run(() => adapter.addGroup(group, {
        mountingMethod: method,
        prepareGroups: freshCart => {
        acceptCart(freshCart);
        const existing = view.groups.find(item => item.id === group.id && item.complete);
        const design = value => JSON.stringify([value.name, value.choices.map(({ letter, size, colour }) => [letter, size, colour])]);
        if (existing && design(existing.snapshot) !== design(group)) {
          const id = window.crypto?.randomUUID?.() || `name-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          group = drafts.importGroup({ ...group, id });
          if (!group || drafts.storageAvailable() === false) throw Object.assign(new Error(), { code: 'RECOVERY_STORAGE_UNAVAILABLE' });
        }
        return [group];
        }
      }), response => {
        notice = t(response.status === 'already-present' ? 'already_added' : 'added', { name: group.name });
        setProductMessage(notice);
      });
      if (!result && lastErrorCode === 'CART_REVIEW_REQUIRED' && !recovery() && view?.groups.some(item => item.id === group.id && item.complete)) errorMessage = t('selection_changed');
      if (result || recovery() || (errorMessage && view?.groups.some(item => item.id === group.id))) {
        opener = document.activeElement;
        if (bag && !bag.open) bag.showModal();
        render();
        if (!result) bag?.querySelector('[data-commerce-error]')?.focus();
      } else if (errorMessage) {
        setProductMessage(errorMessage, true); root.querySelector('[data-commerce-product-error]')?.focus();
      }
    };
    const checkout = async () => {
      if (busy || stale || recovery() || !cart?.items?.length || view?.issues?.length) return;
      const fingerprint = value => JSON.stringify({ ...value, attributes: { ...value.attributes, '8 cm mounting': undefined, '12 cm mounting': undefined, ...(!hasHeight('Medium') ? { 'Mounting method': undefined } : {}) } });
      const previous = fingerprint(cart);
      await run(() => adapter.verifyCheckout(), result => {
        if (fingerprint(result.cart) !== previous) { notice = t('checkout_changed'); return; }
        const target = new URL(result.checkoutUrl, window.location.origin);
        if (target.origin !== window.location.origin || target.pathname !== config.routesRoot + 'checkout' || target.search || target.hash) throw Object.assign(new Error(), { code: 'INVALID_ROUTES' });
        window.location.assign(target.href);
      });
    };
    on(root, 'click', event => {
      if (event.target.closest('[data-mounting-change]') && root.tcBuilder) {
        event.preventDefault(); bag?.close(); root.tcShell?.showPage('product');
        root.querySelector('[data-size="Medium"]')?.click();
        mountingPanel?.scrollIntoView({ block: 'center' });
        mountingPanel?.querySelector('input')?.focus();
        return;
      }
      const control = event.target.closest('[data-commerce-action],[data-commerce-continue]');
      if (!control) return;
      if (control.hasAttribute('data-commerce-continue')) {
        if (root.tcBuilder) {
          event.preventDefault(); root.tcBuilder.createAnother(); root.tcShell?.showPage('product'); bag?.close();
          opener = root.querySelector('#tc-demo-name'); opener?.focus();
        } else { drafts?.startAnother(); }
        return;
      }
      if (control.disabled || busy) return;
      const action = control.dataset.commerceAction;
      const id = control.dataset.commerceGroup;
      const group = view?.groups.find(item => item.id === id);
      if (action === 'quantity') return;
      event.preventDefault();
      if (action === 'refresh') refresh();
      else if (action === 'checkout') checkout();
      else if (action === 'repair') run(() => adapter.repairPending(), () => { notice = t('recovery_done'); });
      else if (action === 'discard') run(() => adapter.discardPending(), () => { notice = t('recovery_done'); });
      else if (action === 'remove' && group) run(() => adapter.removeGroup(id), () => { notice = t('removed', { name: group.name }); });
      else if (action === 'edit' && group?.complete) {
        const hostIndex = hosts.findIndex(host => host.contains(control));
        editId = id; render();
        hosts[hostIndex]?.querySelector('[data-commerce-action="confirm-edit"]')?.focus();
      } else if (action === 'cancel-edit') { editId = null; render(); }
      else if (action === 'confirm-edit' && group?.complete) {
        const snapshot = clone(group.snapshot);
        if (!drafts?.importGroup(snapshot) || drafts.storageAvailable() === false) { exposeError({ code: 'RECOVERY_STORAGE_UNAVAILABLE' }); return; }
        run(() => adapter.removeGroup(id), () => {
          if (root.tcBuilder?.importName(snapshot)) {
            root.tcShell?.showPage('product'); bag?.close(); opener = root.querySelector('#tc-demo-name'); opener?.focus();
            setProductMessage(t('edit_removed'));
          } else {
            const saved = drafts?.importGroup(snapshot);
            if (!saved || drafts.storageAvailable() === false) throw Object.assign(new Error(), { code: 'RECOVERY_STORAGE_UNAVAILABLE' });
            window.location.assign(builderUrl);
          }
        });
      }
    });
    on(root, 'change', async event => {
      const input = event.target.closest('[data-commerce-mounting]');
      if (!input || busy || selectedHeight() !== 'Medium' || !mountingOptions.includes(input.value)) return;
      const value = input.value;
      mountingChoice = value;
      try { window.localStorage.setItem('together-created:mounting:v1', value); } catch (_) {}
      if (hasHeight('Medium')) await run(() => adapter.setMountingMethod(value), () => { notice = t('mounting_saved'); });
      renderQuote();
      mountingPanel?.querySelectorAll('input').forEach(el => { if (el.value === value) el.focus(); });
    });
    on(root, 'submit', async event => {
      const form = event.target.closest('[data-commerce-quantity]');
      if (!form) return;
      event.preventDefault();
      if (busy || recovery()) return;
      const value = form.elements.quantity.value;
      const quantity = Number(value);
      const group = view?.groups.find(item => item.id === form.dataset.commerceQuantity);
      if (!/^\d+$/.test(value) || !Number.isSafeInteger(quantity) || quantity < 1) { exposeError({ code: 'INVALID_QUANTITY' }); return; }
      if (!group?.complete) return;
      const inputHadFocus = document.activeElement === form.elements.quantity;
      const formHost = form.closest('[data-commerce-cart]');
      const result = await run(() => adapter.setGroupQuantity(group.id, quantity), () => { notice = t('updated', { name: group.name }); });
      if (inputHadFocus && result) [...root.querySelectorAll('[data-commerce-quantity]')].find(updated => updated.dataset.commerceQuantity === group.id && updated.closest('[data-commerce-cart]') === formHost)?.elements.quantity.focus();
    });
    on(root, 'tc:selection-change', event => { selection = event.detail; setProductMessage('', true); setProductMessage(''); renderQuote(); });
    on(bag, 'close', () => { if (opener?.isConnected) opener.focus(); });
    setHidden('[data-commerce-draft],[data-commerce-draft-delivery]', true);
    root.tcCommerceUI = {
      openBag, addCurrent, refresh,
      getState: () => ({ busy, stale, blocked: busy || stale || !!recovery() || !!view?.issues?.length, cart: cart ? clone(cart) : null, error: errorMessage }),
      destroy() {
        root.tcShippingUI?.destroy();
        destroyed = true; lifecycle.abort();
        originals.forEach((original, el) => { if (original.restoreText) el.textContent = original.text; el.hidden = original.hidden; if (typeof original.disabled === 'boolean') el.disabled = original.disabled; if (original.label === null) el.removeAttribute('aria-label'); else el.setAttribute('aria-label', original.label); });
        root.querySelectorAll('[data-commerce-quote-lines]').forEach(list => { list.replaceChildren(); list.hidden = true; });
        mountingPanel?.replaceChildren();
        if (mountingPanel) mountingPanel.hidden = true;
        hosts.forEach(host => { host.replaceChildren(); host.hidden = true; host.removeAttribute('aria-busy'); });
        if (root.tcCommerceUI === this) delete root.tcCommerceUI;
      }
    };
    render(); refresh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true }); else init();
  document.addEventListener('shopify:section:load', init);
})();




